import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

// Convert HTML br tags to newlines for textarea display
const htmlToText = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html?.replace(/<br\s*\/?>/gi, '\n')?.replace(/<\/p>/gi, '\n')?.replace(/<p[^>]*>/gi, '')?.replace(/<[^>]+>/g, (match) => {
      // Keep formatting tags as-is (bold, italic, etc.)
      return match;
    });
};

// Convert newlines to HTML br tags for storage/preview
const textToHtml = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text?.replace(/\n/g, '<br/>');
};

const RichTextEditor = ({ value, onChange, error, compact = false }) => {
  const [activeTab, setActiveTab] = useState('edit');
  
  // Convert stored HTML value to display text (br -> newlines)
  const displayValue = (() => {
    if (!value || typeof value !== 'string') return '';
    return value?.replace(/<br\s*\/?>/gi, '\n');
  })();

  const toolbarButtons = [
    { icon: 'Bold', label: 'Kalın', tag: 'strong' },
    { icon: 'Italic', label: 'İtalik', tag: 'em' },
    { icon: 'Underline', label: 'Altı Çizili', tag: 'u' },
    { icon: 'List', label: 'Liste', tag: 'ul' },
    { icon: 'ListOrdered', label: 'Numaralı Liste', tag: 'ol' },
    { icon: 'Link', label: 'Link', tag: 'a' }
  ];
  
  const handleFormat = (tag) => {
    const textarea = document.getElementById('description-editor');
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const selectedText = displayValue?.substring(start, end);
    
    let formattedText = '';
    if (tag === 'ul' || tag === 'ol') {
      formattedText = `\n• ${selectedText}\n`;
    } else if (tag === 'a') {
      formattedText = `[${selectedText}](url)`;
    } else {
      formattedText = `<${tag}>${selectedText}</${tag}>`;
    }
    
    const newDisplayValue = displayValue?.substring(0, start) + formattedText + displayValue?.substring(end);
    // Convert back to HTML for storage
    onChange(newDisplayValue?.replace(/\n/g, '<br/>'));
  };

  const handleTextareaChange = (e) => {
    const textValue = e?.target?.value;
    // Convert newlines to <br/> for storage
    onChange(textValue?.replace(/\n/g, '<br/>'));
  };

  const minHeight = compact ? 'min-h-[150px]' : 'min-h-[300px] md:min-h-[400px]';
  
  if (compact) {
    return (
      <div>
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between bg-muted px-3 py-2 border-b border-border">
            <div className="flex items-center gap-1 overflow-x-auto">
              {toolbarButtons?.map((btn) => (
                <button
                  key={btn?.tag}
                  type="button"
                  onClick={() => handleFormat(btn?.tag)}
                  className="p-1.5 hover:bg-background rounded transition-smooth"
                  title={btn?.label}
                >
                  <Icon name={btn?.icon} size={16} />
                </button>
              ))}
            </div>
            <div className="flex gap-1 ml-2">
              <button
                type="button"
                onClick={() => setActiveTab('edit')}
                className={`px-2 py-1 text-xs rounded transition-smooth ${
                  activeTab === 'edit' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Düzenle
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('preview')}
                className={`px-2 py-1 text-xs rounded transition-smooth ${
                  activeTab === 'preview' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Önizle
              </button>
            </div>
          </div>
          {activeTab === 'edit' ? (
            <textarea
              id="description-editor"
              name="description"
              value={displayValue}
              onChange={handleTextareaChange}
              placeholder="Etkinliğinizin detaylı açıklamasını yazın..."
              className="w-full min-h-[150px] p-3 text-sm text-foreground bg-background border-0 focus:outline-none resize-none"
              style={{ fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}
            />
          ) : (
            <div className="min-h-[150px] p-3 text-sm text-foreground bg-background prose prose-sm max-w-none">
              {value ? (
                <div dangerouslySetInnerHTML={{ __html: typeof value === 'string' ? value : '' }} />
              ) : (
                <p className="text-muted-foreground italic">Önizleme için açıklama yazın...</p>
              )}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1 text-xs text-error">{error}</p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          Metni biçimlendirmek için araç çubuğunu kullanın.
        </p>
      </div>
    );
  }
  
  return (
    <div className="bg-card rounded-lg md:rounded-xl lg:rounded-2xl p-4 md:p-6 lg:p-8 shadow-organic">
      <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-4 md:mb-5 lg:mb-6">
        Etkinlik Açıklaması
      </h2>
      <div className="border border-border rounded-lg md:rounded-xl overflow-hidden">
        <div className="flex items-center justify-between bg-muted px-3 md:px-4 py-2 md:py-3 border-b border-border">
          <div className="flex items-center gap-1 md:gap-2 overflow-x-auto">
            {toolbarButtons?.map((btn) => (
              <button
                key={btn?.tag}
                type="button"
                onClick={() => handleFormat(btn?.tag)}
                className="p-2 hover:bg-background rounded transition-smooth"
                title={btn?.label}
              >
                <Icon name={btn?.icon} size={18} />
              </button>
            ))}
          </div>
          
          <div className="flex gap-2 ml-2">
            <button
              type="button"
              onClick={() => setActiveTab('edit')}
              className={`px-3 py-1 text-sm rounded transition-smooth ${
                activeTab === 'edit' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Düzenle
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('preview')}
              className={`px-3 py-1 text-sm rounded transition-smooth ${
                activeTab === 'preview' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Önizle
            </button>
          </div>
        </div>
        
        {activeTab === 'edit' ? (
          <textarea
            id="description-editor"
            name="description"
            value={displayValue}
            onChange={handleTextareaChange}
            placeholder="Etkinliğinizin detaylı açıklamasını yazın...\n\nNeleri içerir?\nKatılımcılar ne beklemelidir?\nÖzel gereksinimler var mı?"
            className="w-full min-h-[300px] md:min-h-[400px] p-4 md:p-6 text-sm md:text-base text-foreground bg-background border-0 focus:outline-none resize-none"
            style={{ fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}
          />
        ) : (
          <div className="min-h-[300px] md:min-h-[400px] p-4 md:p-6 text-sm md:text-base text-foreground bg-background prose prose-sm md:prose-base max-w-none">
            {value ? (
              <div dangerouslySetInnerHTML={{ __html: typeof value === 'string' ? value : '' }} />
            ) : (
              <p className="text-muted-foreground italic">Önizleme için açıklama yazın...</p>
            )}
          </div>
        )}
      </div>
      {error && (
        <p className="mt-2 text-sm text-error">{error}</p>
      )}
      <p className="mt-2 text-xs md:text-sm text-muted-foreground">
        Metni biçimlendirmek için araç çubuğunu kullanın. Markdown ve HTML desteklenir.
      </p>
    </div>
  );
};

export default RichTextEditor;